// Copyright (c) 2021. Sendanor <info@sendanor.fi>. All rights reserved.

import Observer, { ObserverCallback, ObserverDestructor } from "../nor/ts/Observer";
import LogService from "../nor/ts/LogService";
import { isString } from "../nor/ts/modules/lodash";
import JsonAny from "../nor/ts/Json";
import { LOGIN_URL, LOGOUT_URL } from "../constants/form-backend";
import HttpService from "./HttpService";

const LOG = LogService.createLogger('SessionService');

export enum SessionServiceEvent {

    CHANGED     = "SessionService:changed",
    LOGIN_ERROR = "SessionService:loginError"

}

export type SessionServiceDestructor = ObserverDestructor;

export class SessionService {

    private static _sessionId : string | undefined;

    private static _observer: Observer<SessionServiceEvent> = new Observer<SessionServiceEvent>("SessionService");

    public static Event = SessionServiceEvent;

    public static on (
        name: SessionServiceEvent,
        callback: ObserverCallback<SessionServiceEvent>
    ): SessionServiceDestructor {
        return this._observer.listenEvent(name, callback);
    }

    public static destroy (): void {
        this._observer.destroy();
    }

    public static hasSession () : boolean {
        return this._sessionId !== undefined;
    }

    public static getSessionId () : string | undefined {
        return this._sessionId;
    }

    public static login (username: string, password: string) {

        HttpService.postJson(`${LOGIN_URL}`, {
            username,
            password
        }).then( (response : JsonAny | undefined) => {

            const sessionId : any = (response as any)?.payload;

            if ( isString(sessionId) && sessionId ) {

                this._sessionId = sessionId;
                this._observer.triggerEvent(SessionServiceEvent.CHANGED);

                LOG.debug(`sessionId: ${sessionId}`);

            } else {
                throw new TypeError(`Session id invalid: "${sessionId}"`);
            }
        }).catch( (err : any) => {
            LOG.error(`Login error: `, err);
            this._observer.triggerEvent(SessionServiceEvent.LOGIN_ERROR, err);
        });

    }

    public static logout () {

        HttpService.postJson(`${LOGOUT_URL}`).catch( (err : any) => {
            LOG.error(`Logout error: `, err);
        }).finally( () => {

            this._sessionId = undefined;
            this._observer.triggerEvent(SessionServiceEvent.CHANGED);
            LOG.debug(`sessionId cleared`);

        });

    }

}

export default SessionService;
